import React, { useEffect, useState } from 'react'
import TopBar from './TopBar'
import Footer from './Footer'
import { connect } from 'react-redux'
import { useHistory, useLocation, Route, Switch } from 'react-router-dom'
import './AdminLayout.css'
import '../styles/components.css'
import { useThemeContext } from '../contexts/ThemeContext'
import AdminTenantManagement from '../components/AdminTenantManagement'
import AdminUserManagement from '../components/AdminUserManagement'
import AdminMeltwater from '../components/AdminMeltwater'
import AdminS3Upload from '../components/AdminS3Upload'
import AdminFileUpload from '../components/AdminFileUpload'
import AdminJobsPanel from '../components/AdminJobsPanel'
import AdminTVUpload from '../components/AdminTVUpload'
import MediaPanel from '../views/MediaPanel'
import EventsPanel from '../views/EventsPanel'
import AdminTVBroadcastManager from '../components/AdminTVBroadcastManager'
import DetectionReviewPanel from '../views/DetectionReviewPanel'
import VideoMetadata from '../views/VideoMetadata'
import AdminSponsorObjects from '../components/AdminSponsorObjects'
import PlacementMappingReview from '../views/PlacementMappingReview'
import AdminCpmRates from '../components/AdminCpmRates'
import AdminAgentAccess from '../components/AdminAgentAccess'
import ApiService from '../services/ApiService'

const DataManagementPage = () => (
  <>
    <AdminMeltwater />
    <AdminS3Upload />
    <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <AdminFileUpload type="Medietall" accept=".xlsx" apiRequest={ApiService.importMedietall} />
    </div>
  </>
);

const JobsProcessingPage = () => (
  <>
    <AdminJobsPanel />
    <AdminTVUpload />
  </>
);

const sidebarItems = [
  {
    id: 'admin',
    label: 'Admin',
    path: '/admin',
    children: [
      { id: 'tenants', label: 'Tenants', path: '/admin' },
      { id: 'users', label: 'Users', path: '/admin/users' },
      { id: 'data', label: 'Data Management', path: '/admin/data' },
      { id: 'jobs', label: 'Jobs & Processing', path: '/admin/jobs' },
    ],
  },
  { id: 'media', label: 'Media', path: '/admin/media' },
  { id: 'events', label: 'Events', path: '/admin/events' },
  { id: 'broadcasts', label: 'TV Broadcasts', path: '/admin/broadcasts' },
  { id: 'video-metadata', label: 'Video Metadata', path: '/admin/video-metadata' },
  { id: 'reviews', label: 'Detection Reviews', path: '/admin/reviews' },
  { id: 'sponsor-objects', label: 'Sponsor Objects', path: '/admin/sponsor-objects' },
  { id: 'placement-reviews', label: 'Placement Reviews', path: '/admin/placement-reviews' },
  { id: 'cpm-rates', label: 'CPM Rates', path: '/admin/cpm-rates' },
  { id: 'agent-access', label: 'Agent Access', path: '/admin/agent-access' },
];

// Build a flat list of all navigable items for path matching
const allNavItems = sidebarItems.flatMap(item =>
  item.children ? [item, ...item.children] : [item]
);

const AdminLayout = ({toggleRightSidebar, topbarTheme, tenant, children, date}) => {
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [activeId, setActiveId] = useState('tenants');
  const history = useHistory();
  const location = useLocation();
  const { isDark } = useThemeContext();

  const openMenu = () => {
    setIsMenuOpened(!isMenuOpened);
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const path = location.pathname;
    // Match longest path first so /admin/users beats /admin
    const sorted = [...allNavItems].sort((a, b) => b.path.length - a.path.length);
    for (const item of sorted) {
      if (path === item.path || (item.path !== '/admin' && path.startsWith(item.path))) {
        setActiveId(item.id);
        return;
      }
    }
    if (path === '/admin') {
      setActiveId('tenants');
    }
  }, [location.pathname]);

  const handleNavClick = (item) => {
    setActiveId(item.id);
    history.push(item.path);
  };

  // Check if a parent item or any of its children is active
  const isParentActive = (item) => {
    if (activeId === item.id) return true;
    if (item.children) {
      return item.children.some(child => child.id === activeId);
    }
    return false;
  };

  return (
    <div id="layout-wrapper" className="admin-layout">
      <TopBar
        theme={topbarTheme}
        isMenuOpened={isMenuOpened}
        toggleRightSidebar={toggleRightSidebar}
        openLeftMenuCallBack={openMenu}
        tenant={tenant}
      />
      <div className="main-content" id="admin-layout">
        <div className="admin-grid">
          <nav className="admin-sidebar">
            {sidebarItems.map((item) => (
              <div key={item.id}>
                <button
                  className={`admin-sidebar-item${isParentActive(item) ? ' active' : ''}`}
                  onClick={() => handleNavClick(item.children ? item.children[0] : item)}
                >
                  {item.label}
                </button>
                {item.children && isParentActive(item) && (
                  <div className="admin-sidebar-children">
                    {item.children.map((child) => (
                      <button
                        key={child.id}
                        className={`admin-sidebar-child${activeId === child.id ? ' active' : ''}`}
                        onClick={() => handleNavClick(child)}
                      >
                        {child.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="admin-body">
            <Switch>
              <Route exact path="/admin" component={AdminTenantManagement} />
              <Route exact path="/admin/users" component={AdminUserManagement} />
              <Route exact path="/admin/data" component={DataManagementPage} />
              <Route exact path="/admin/jobs" component={JobsProcessingPage} />
              <Route exact path="/admin/media" component={MediaPanel} />
              <Route exact path="/admin/events" component={EventsPanel} />
              <Route exact path="/admin/video-metadata" component={VideoMetadata} />
              <Route exact path="/admin/broadcasts" component={AdminTVBroadcastManager} />
              <Route exact path="/admin/reviews" component={DetectionReviewPanel} />
              <Route exact path="/admin/sponsor-objects" component={AdminSponsorObjects} />
              <Route exact path="/admin/placement-reviews" component={PlacementMappingReview} />
              <Route exact path="/admin/cpm-rates" component={AdminCpmRates} />
              <Route exact path="/admin/agent-access" component={AdminAgentAccess} />
            </Switch>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const mapStateToProps = state => ({
  tenant: state.auth.tenant,
  date: state.date
})

export default connect(mapStateToProps, {})(AdminLayout)
